import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import Map, { Marker, Source, Layer } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Flag, Target, Loader2,
  Crosshair, CircleDot, Locate, Compass, Navigation2, Undo2, Trash2, Check
} from 'lucide-react';
import { API, MAPBOX_TOKEN } from './constants';
import { ShotTracker } from './ShotTracker';

const GPS_ALPHA = 0.35;
const GPS_MAX_ACCURACY = 30;

// Generate a GeoJSON circle polygon from a center point
const makeCircle = (center, radiusMeters, steps = 64) => {
  const coords = [];
  const km = radiusMeters / 1000;
  const lat = center.lat * Math.PI / 180;
  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    const dLat = (km / 6371) * Math.cos(angle) * (180 / Math.PI);
    const dLng = (km / 6371) * Math.sin(angle) * (180 / Math.PI) / Math.cos(lat);
    coords.push([center.lng + dLng, center.lat + dLat]);
  }
  return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } };
};

// Convert {lat,lng}[] polygon to GeoJSON Feature
const polyToGeoJSON = (points, props = {}) => ({
  type: 'Feature',
  properties: props,
  geometry: {
    type: 'Polygon',
    coordinates: [points.map(p => [p.lng, p.lat]).concat([[points[0].lng, points[0].lat]])]
  }
});

// Convert {lat,lng}[] to GeoJSON LineString
const lineToGeoJSON = (points, props = {}) => ({
  type: 'Feature',
  properties: props,
  geometry: {
    type: 'LineString',
    coordinates: points.map(p => [p.lng, p.lat])
  }
});

export const GPSMapView = ({ activeRound, currentHole, totalHoles, holesData, onClose, onHoleChange, shots = [], onLogShot }) => {
  const mapRef = useRef(null);
  const [rawPos, setRawPos] = useState(null);
  const [userPos, setUserPos] = useState(null);
  const [greenPos, setGreenPos] = useState(null);
  const [teePos, setTeePos] = useState(null);
  const [osmData, setOsmData] = useState(null);
  const [aimpoint, setAimpoint] = useState(null);
  const [distToGreen, setDistToGreen] = useState(null);
  const [distFront, setDistFront] = useState(null);
  const [distBack, setDistBack] = useState(null);
  const [distToAim, setDistToAim] = useState(null);
  const [distAimToGreen, setDistAimToGreen] = useState(null);
  const [gpsReady, setGpsReady] = useState(false);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [placingGreen, setPlacingGreen] = useState(false);
  const [savedMarkers, setSavedMarkers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showArcs, setShowArcs] = useState(true);
  const [headingMode, setHeadingMode] = useState('hole');
  const [holeBearing, setHoleBearing] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const initialFitDone = useRef(false);
  const placingGreenRef = useRef(false);
  const smoothedRef = useRef(null);
  const watchIdRef = useRef(null);
  const [showShotTracker, setShowShotTracker] = useState(false);
  const [clubRecommendation, setClubRecommendation] = useState(null);
  const [isDraggingAim, setIsDraggingAim] = useState(false);
  const [pendingGreen, setPendingGreen] = useState(null); // Confirmation step for green placement

  const [viewState, setViewState] = useState({
    longitude: -98.5, latitude: 33, zoom: 17, bearing: 0, pitch: 0
  });

  const courseLat = activeRound?.latitude;
  const courseLng = activeRound?.longitude;
  const courseId = activeRound?.course_id;
  const holeData = holesData?.[currentHole - 1] || {};

  useEffect(() => { placingGreenRef.current = placingGreen; }, [placingGreen]);

  const calcYards = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 1.09361);
  };

  const calcBearing = (lat1, lng1, lat2, lng2) => {
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const y = Math.sin(dLng) * Math.cos(lat2 * Math.PI / 180);
    const x = Math.cos(lat1 * Math.PI / 180) * Math.sin(lat2 * Math.PI / 180) - Math.sin(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.cos(dLng);
    return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
  };

  const computeDefaultAimpoint = useCallback((osmHole, green, tee, par) => {
    if (!green) return null;
    if (par <= 3) return green;
    if (osmHole?.path?.length >= 3) {
      const ratio = par >= 5 ? 0.4 : 0.55;
      const idx = Math.floor((osmHole.path.length - 1) * ratio);
      return osmHole.path[idx];
    }
    if (tee && green) {
      const ratio = par >= 5 ? 0.4 : 0.55;
      return { lat: tee.lat + (green.lat - tee.lat) * ratio, lng: tee.lng + (green.lng - tee.lng) * ratio };
    }
    return green;
  }, []);

  // Fetch OSM data & saved markers
  useEffect(() => {
    if (!courseLat || !courseLng) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      axios.get(`${API}/golf/course-holes-osm?lat=${courseLat}&lng=${courseLng}`).catch(() => ({ data: { holes: [], hazards: [], bunkers: [] } })),
      courseId ? axios.get(`${API}/golf/hole-markers/${courseId}`).catch(() => ({ data: { markers: [] } })) : Promise.resolve({ data: { markers: [] } }),
    ]).then(([osmRes, markersRes]) => {
      setOsmData({
        holes: osmRes.data.holes || [],
        hazards: osmRes.data.hazards || [],
        bunkers: osmRes.data.bunkers || [],
        source: osmRes.data.source,
      });
      const m = {};
      (markersRes.data.markers || []).forEach(mk => { m[mk.hole_number] = { lat: mk.green_lat, lng: mk.green_lng }; });
      setSavedMarkers(m);
      setLoading(false);
    });
  }, [courseLat, courseLng, courseId]);

  // Set green/tee positions per hole
  useEffect(() => {
    if (!osmData) return;
    const osmHole = osmData.holes.find(h => h.hole_number === currentHole);
    const saved = savedMarkers[currentHole];
    const par = holeData.par || 4;
    let newGreen, newTee;
    if (osmHole) {
      newGreen = osmHole.green_center || { lat: osmHole.green_lat, lng: osmHole.green_lng };
      newTee = { lat: osmHole.tee_lat, lng: osmHole.tee_lng };
      setGreenPos(newGreen);
      setTeePos(newTee);
      setPlacingGreen(false);
    } else if (saved) {
      newGreen = saved;
      newTee = null;
      setGreenPos(newGreen);
      setTeePos(null);
      setPlacingGreen(false);
    } else {
      setGreenPos(null);
      setTeePos(null);
      setPlacingGreen(true);
      setAimpoint(null);
      initialFitDone.current = false;
      return;
    }
    setAimpoint(computeDefaultAimpoint(osmHole, newGreen, newTee, par));
    initialFitDone.current = false;
  }, [currentHole, osmData, savedMarkers, holeData.par, computeDefaultAimpoint]);

  // Compute hole bearing
  useEffect(() => {
    if (greenPos && teePos) {
      setHoleBearing(calcBearing(teePos.lat, teePos.lng, greenPos.lat, greenPos.lng));
    } else if (greenPos && userPos) {
      setHoleBearing(prev => prev !== null ? prev : calcBearing(userPos.lat, userPos.lng, greenPos.lat, greenPos.lng));
    } else {
      setHoleBearing(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [greenPos, teePos, userPos ? 1 : 0]);

  // GPS watch
  useEffect(() => {
    if (!navigator.geolocation) { setGpsReady(true); return; }
    const handlePos = (pos) => {
      const acc = Math.round(pos.coords.accuracy);
      setGpsAccuracy(acc);
      setRawPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      if (acc > GPS_MAX_ACCURACY) return;
      const raw = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      if (!smoothedRef.current) {
        smoothedRef.current = raw;
      } else {
        smoothedRef.current = {
          lat: GPS_ALPHA * raw.lat + (1 - GPS_ALPHA) * smoothedRef.current.lat,
          lng: GPS_ALPHA * raw.lng + (1 - GPS_ALPHA) * smoothedRef.current.lng,
        };
      }
      setUserPos({ ...smoothedRef.current });
      setGpsReady(true);
    };
    navigator.geolocation.getCurrentPosition(handlePos, () => setGpsReady(true), { enableHighAccuracy: true, timeout: 10000 });
    watchIdRef.current = navigator.geolocation.watchPosition(handlePos, () => {}, { enableHighAccuracy: true, maximumAge: 2000, timeout: 10000 });
    return () => { if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current); };
  }, []);

  // Compute distances
  useEffect(() => {
    if (aimpoint && greenPos) {
      setDistAimToGreen(calcYards(aimpoint.lat, aimpoint.lng, greenPos.lat, greenPos.lng));
    } else {
      setDistAimToGreen(null);
    }
    if (userPos && aimpoint) {
      setDistToAim(calcYards(userPos.lat, userPos.lng, aimpoint.lat, aimpoint.lng));
    } else {
      setDistToAim(null);
    }
    if (userPos && greenPos) {
      const dCenter = calcYards(userPos.lat, userPos.lng, greenPos.lat, greenPos.lng);
      setDistToGreen(dCenter);
      const osmHole = osmData?.holes?.find(h => h.hole_number === currentHole);
      if (osmHole?.green_front && osmHole?.green_back) {
        setDistFront(calcYards(userPos.lat, userPos.lng, osmHole.green_front.lat, osmHole.green_front.lng));
        setDistBack(calcYards(userPos.lat, userPos.lng, osmHole.green_back.lat, osmHole.green_back.lng));
      } else {
        setDistFront(Math.max(0, dCenter - 12));
        setDistBack(dCenter + 12);
      }
    } else {
      setDistToGreen(null); setDistFront(null); setDistBack(null);
    }
  }, [userPos, greenPos, aimpoint, osmData, currentHole]);

  // Fit bounds when hole changes or data loads
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || initialFitDone.current) return;
    const map = mapRef.current;

    const targetBearing = headingMode === 'hole' && holeBearing !== null ? holeBearing : 0;
    const refPoint = teePos || greenPos;

    if (refPoint && greenPos) {
      // We have hole data — fit to tee+green
      const bounds = new mapboxgl.LngLatBounds();
      if (teePos) {
        bounds.extend([teePos.lng, teePos.lat]);
      } else {
        const yds = holeData.yardage || 350;
        const metersBack = yds * 0.9144;
        const bearing = courseLat && courseLng
          ? calcBearing(greenPos.lat, greenPos.lng, courseLat, courseLng)
          : 180;
        const latOffset = (metersBack / 111320) * Math.cos(bearing * Math.PI / 180);
        const lngOffset = (metersBack / (111320 * Math.cos(greenPos.lat * Math.PI / 180))) * Math.sin(bearing * Math.PI / 180);
        bounds.extend([greenPos.lng + lngOffset, greenPos.lat + latOffset]);
      }
      bounds.extend([greenPos.lng, greenPos.lat]);
      if (userPos) {
        const distUserToGreen = calcYards(userPos.lat, userPos.lng, greenPos.lat, greenPos.lng);
        if (distUserToGreen < 800) {
          bounds.extend([userPos.lng, userPos.lat]);
        }
      }
      map.fitBounds(bounds, {
        padding: { top: 130, bottom: 220, left: 80, right: 80 },
        bearing: targetBearing,
        pitch: 0,
        duration: 800
      });
      initialFitDone.current = true;
    } else if (placingGreen && courseLat && courseLng) {
      // Placing green mode — always center on COURSE, not user position
      // Use zoom 16 to show enough of the course for hole identification
      map.flyTo({ center: [courseLng, courseLat], zoom: 16, bearing: 0, duration: 800 });
      initialFitDone.current = true;
    } else if (!initialFitDone.current) {
      // Fallback — center on course location, then user position
      const center = (courseLat && courseLng) ? { lat: courseLat, lng: courseLng } : userPos;
      if (center) {
        map.flyTo({ center: [center.lng, center.lat], zoom: 17, bearing: targetBearing, duration: 800 });
      }
      initialFitDone.current = true;
    }
  }, [mapLoaded, greenPos, teePos, userPos, holeBearing, headingMode, holeData.yardage, courseLat, courseLng, placingGreen]);

  // Club recommendation
  useEffect(() => {
    if (!distToGreen || distToGreen < 10) { setClubRecommendation(null); return; }
    const fetchRec = async () => {
      try {
        const res = await axios.get(`${API}/golf/club-recommendation?distance=${distToGreen}`);
        setClubRecommendation(res.data);
      } catch { setClubRecommendation(null); }
    };
    fetchRec();
  }, [distToGreen]);

  // Map click handler
  const handleMapClick = useCallback((e) => {
    const pos = { lat: e.lngLat.lat, lng: e.lngLat.lng };
    if (placingGreenRef.current) {
      // Don't save immediately — show confirmation first
      setPendingGreen(pos);
    } else {
      setAimpoint(pos);
    }
  }, []);

  // Confirm green placement
  const confirmGreen = useCallback(() => {
    if (!pendingGreen) return;
    setGreenPos(pendingGreen);
    setPlacingGreen(false);
    if (courseId) {
      axios.post(`${API}/golf/hole-markers`, {
        course_id: String(courseId), hole_number: currentHole,
        green_lat: pendingGreen.lat, green_lng: pendingGreen.lng
      }).catch(() => {});
      setSavedMarkers(prev => ({ ...prev, [currentHole]: pendingGreen }));
    }
    setPendingGreen(null);
  }, [pendingGreen, courseId, currentHole]);

  // Cancel pending green placement
  const cancelPendingGreen = useCallback(() => {
    setPendingGreen(null);
  }, []);

  // Delete a saved green marker
  const deleteGreenMarker = useCallback(() => {
    if (!courseId) return;
    axios.delete(`${API}/golf/hole-markers/${courseId}/${currentHole}`).catch(() => {});
    setSavedMarkers(prev => {
      const next = { ...prev };
      delete next[currentHole];
      return next;
    });
    setGreenPos(null);
    setTeePos(null);
    setAimpoint(null);
    setPlacingGreen(true);
    initialFitDone.current = false;
  }, [courseId, currentHole]);

  // Build GeoJSON data for layers
  const layerData = useMemo(() => {
    const features = { arcs: [], bunkers: [], hazards: [], fairway: null, lines: [] };

    // Distance arcs
    if (greenPos && showArcs) {
      [100, 150, 200, 250].forEach(yds => {
        const radiusMeters = yds / 1.09361;
        features.arcs.push({ ...makeCircle(greenPos, radiusMeters), properties: { yds, is150: yds === 150 } });
      });
    }

    // Bunkers
    (osmData?.bunkers || []).forEach((b, i) => {
      if (b.polygon?.length > 2) features.bunkers.push(polyToGeoJSON(b.polygon, { id: i }));
    });

    // Hazards
    (osmData?.hazards || []).forEach((h, i) => {
      if (h.polygon?.length > 2) features.hazards.push(polyToGeoJSON(h.polygon, { id: i }));
    });

    // Fairway path
    const osmHole = osmData?.holes?.find(h => h.hole_number === currentHole);
    if (osmHole?.path?.length > 1) {
      features.fairway = lineToGeoJSON(osmHole.path);
    }

    // Aimpoint line to green
    if (aimpoint && greenPos) {
      features.lines.push(lineToGeoJSON([aimpoint, greenPos], { type: 'aim-to-green' }));
      if (userPos) {
        features.lines.push(lineToGeoJSON([userPos, aimpoint], { type: 'user-to-aim' }));
      }
    } else if (userPos && greenPos) {
      features.lines.push(lineToGeoJSON([userPos, greenPos], { type: 'user-to-green' }));
    }

    // Green circle
    if (greenPos) {
      const osmH = osmData?.holes?.find(h => h.hole_number === currentHole);
      const r = osmH?.green_depth_yards ? Math.max(12, osmH.green_depth_yards * 0.45) : 14;
      features.greenCircle = makeCircle(greenPos, r);
    }

    return features;
  }, [greenPos, showArcs, osmData, currentHole, aimpoint, userPos]);

  // Shot trail data
  const shotTrailData = useMemo(() => {
    const holeShots = (shots || []).filter(s => s.hole_number === currentHole).sort((a, b) => a.shot_number - b.shot_number);
    if (holeShots.length === 0) return null;
    const points = holeShots.map((s, i) => ({
      type: 'Feature',
      properties: { label: `${i + 1}·${s.club}` },
      geometry: { type: 'Point', coordinates: [s.lng, s.lat] }
    }));
    const lineCoords = holeShots.map(s => [s.lng, s.lat]);
    return {
      points: { type: 'FeatureCollection', features: points },
      line: lineCoords.length > 1 ? { type: 'Feature', geometry: { type: 'LineString', coordinates: lineCoords } } : null,
      markers: holeShots
    };
  }, [shots, currentHole]);

  // Midpoint labels for distance lines
  const distanceLabels = useMemo(() => {
    const labels = [];
    if (aimpoint && greenPos && distAimToGreen !== null && distAimToGreen > 5) {
      labels.push({
        id: 'aim-green',
        lat: (aimpoint.lat + greenPos.lat) / 2,
        lng: (aimpoint.lng + greenPos.lng) / 2,
        text: distAimToGreen,
        color: '#f59e0b',
        bg: 'rgba(0,0,0,0.85)',
        border: 'rgba(245,158,11,0.3)'
      });
    }
    if (userPos && aimpoint && distToAim !== null) {
      labels.push({
        id: 'user-aim',
        lat: (userPos.lat + aimpoint.lat) / 2,
        lng: (userPos.lng + aimpoint.lng) / 2,
        text: distToAim,
        color: '#fff',
        bg: 'rgba(0,0,0,0.85)',
        border: 'rgba(255,255,255,0.2)'
      });
    }
    return labels;
  }, [aimpoint, greenPos, userPos, distAimToGreen, distToAim]);

  // 150-yard label position
  const label150 = useMemo(() => {
    if (!greenPos || !showArcs) return null;
    const radiusMeters = 150 / 1.09361;
    const labelAngle = 45 * Math.PI / 180;
    return {
      lat: greenPos.lat + (radiusMeters / 111320) * Math.cos(labelAngle),
      lng: greenPos.lng + (radiusMeters / (111320 * Math.cos(greenPos.lat * Math.PI / 180))) * Math.sin(labelAngle)
    };
  }, [greenPos, showArcs]);

  // Reference markers: show other mapped holes when placing a green (for orientation)
  const referenceMarkers = useMemo(() => {
    if (!placingGreen && !pendingGreen) return [];
    const markers = [];
    // Show OSM holes
    (osmData?.holes || []).forEach(h => {
      if (h.hole_number === currentHole) return;
      const pos = h.green_center || { lat: h.green_lat, lng: h.green_lng };
      if (pos?.lat && pos?.lng) markers.push({ hole: h.hole_number, ...pos, source: 'osm' });
    });
    // Show user-saved markers
    Object.entries(savedMarkers).forEach(([hNum, pos]) => {
      const num = parseInt(hNum);
      if (num === currentHole) return;
      if (markers.find(m => m.hole === num)) return; // already have OSM for this hole
      markers.push({ hole: num, ...pos, source: 'user' });
    });
    return markers;
  }, [placingGreen, pendingGreen, osmData, savedMarkers, currentHole]);

  const goToHole = (h) => {
    onHoleChange(h);
    initialFitDone.current = false;
    setHoleBearing(null);
    setPendingGreen(null);
  };

  const toggleHeadingMode = () => {
    const newMode = headingMode === 'hole' ? 'north' : 'hole';
    setHeadingMode(newMode);
    const map = mapRef.current;
    if (map) {
      const newBearing = newMode === 'north' ? 0 : (holeBearing || 0);
      map.easeTo({ bearing: newBearing, duration: 500 });
    }
  };

  const recenterMap = () => {
    const map = mapRef.current;
    if (!map) return;
    initialFitDone.current = false;
    const targetBearing = headingMode === 'hole' && holeBearing !== null ? holeBearing : 0;
    if (greenPos) {
      const bounds = new mapboxgl.LngLatBounds();
      if (teePos) {
        bounds.extend([teePos.lng, teePos.lat]);
      } else {
        const yds = holeData.yardage || 350;
        const metersBack = yds * 0.9144;
        const bearing = courseLat && courseLng
          ? calcBearing(greenPos.lat, greenPos.lng, courseLat, courseLng) : 180;
        const latOffset = (metersBack / 111320) * Math.cos(bearing * Math.PI / 180);
        const lngOffset = (metersBack / (111320 * Math.cos(greenPos.lat * Math.PI / 180))) * Math.sin(bearing * Math.PI / 180);
        bounds.extend([greenPos.lng + lngOffset, greenPos.lat + latOffset]);
      }
      bounds.extend([greenPos.lng, greenPos.lat]);
      if (userPos) {
        const distUserToGreen = calcYards(userPos.lat, userPos.lng, greenPos.lat, greenPos.lng);
        if (distUserToGreen < 800) bounds.extend([userPos.lng, userPos.lat]);
      }
      map.fitBounds(bounds, {
        padding: { top: 130, bottom: 220, left: 80, right: 80 },
        bearing: targetBearing,
        duration: 800
      });
    } else if (courseLat && courseLng) {
      // No green data — center on course location for hole mapping
      map.flyTo({ center: [courseLng, courseLat], zoom: 16, bearing: 0, duration: 800 });
    } else if (userPos) {
      map.flyTo({ center: [userPos.lng, userPos.lat], zoom: 17, bearing: targetBearing, duration: 800 });
    }
    initialFitDone.current = true;
  };

  const recenter = () => {
    initialFitDone.current = false;
    const map = mapRef.current;
    if (!map) return;
    const refPoint = teePos || userPos;
    if (refPoint && greenPos) {
      const bounds = new mapboxgl.LngLatBounds();
      bounds.extend([refPoint.lng, refPoint.lat]);
      bounds.extend([greenPos.lng, greenPos.lat]);
      if (userPos) bounds.extend([userPos.lng, userPos.lat]);
      map.fitBounds(bounds, {
        padding: { top: 130, bottom: 220, left: 80, right: 80 },
        bearing: calcBearing(refPoint.lat, refPoint.lng, greenPos.lat, greenPos.lng),
        duration: 800
      });
    } else if (courseLat && courseLng) {
      // No hole data — recenter on course
      map.flyTo({ center: [courseLng, courseLat], zoom: 16, duration: 800 });
    } else if (userPos) {
      map.flyTo({ center: [userPos.lng, userPos.lat], zoom: 17, duration: 800 });
    }
  };

  const resetAimpoint = () => {
    const osmHole = osmData?.holes?.find(h => h.hole_number === currentHole);
    setAimpoint(computeDefaultAimpoint(osmHole, greenPos, teePos, holeData.par || 4));
  };

  const moveGreen = () => { setGreenPos(null); setPlacingGreen(true); setPendingGreen(null); };

  const handleLogShot = (club) => {
    if (!userPos || !onLogShot) return;
    const holeShots = (shots || []).filter(s => s.hole_number === currentHole);
    onLogShot({
      hole_number: currentHole,
      shot_number: holeShots.length + 1,
      club,
      lat: userPos.lat,
      lng: userPos.lng,
    });
    setShowShotTracker(false);
  };

  const hasOsmHole = osmData?.holes?.find(h => h.hole_number === currentHole);
  const hasPreciseGreen = hasOsmHole?.green_front && hasOsmHole?.green_back;

  // GeoJSON collections
  const arcsGeoJSON = useMemo(() => ({ type: 'FeatureCollection', features: layerData.arcs }), [layerData.arcs]);
  const bunkersGeoJSON = useMemo(() => ({ type: 'FeatureCollection', features: layerData.bunkers }), [layerData.bunkers]);
  const hazardsGeoJSON = useMemo(() => ({ type: 'FeatureCollection', features: layerData.hazards }), [layerData.hazards]);
  const linesGeoJSON = useMemo(() => ({ type: 'FeatureCollection', features: layerData.lines }), [layerData.lines]);

  return (
    <div className="fixed inset-0 z-[60] bg-black" data-testid="gps-map-fullscreen">
      <Map
        ref={mapRef}
        {...viewState}
        onMove={(e) => setViewState(e.viewState)}
        onLoad={() => setMapLoaded(true)}
        onClick={handleMapClick}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle="mapbox://styles/mapbox/satellite-v9"
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
        logoPosition="bottom-right"
        maxPitch={0}
        dragRotate={false}
      >
        {/* Distance arcs */}
        <Source id="arcs" type="geojson" data={arcsGeoJSON}>
          <Layer id="arcs-normal" type="line" filter={['!=', ['get', 'is150'], true]}
            paint={{ 'line-color': '#ffffff', 'line-opacity': 0.2, 'line-width': 1 }} />
          <Layer id="arcs-150" type="line" filter={['==', ['get', 'is150'], true]}
            paint={{ 'line-color': '#facc15', 'line-opacity': 0.5, 'line-width': 1.5 }} />
        </Source>

        {/* Bunkers */}
        <Source id="bunkers" type="geojson" data={bunkersGeoJSON}>
          <Layer id="bunkers-fill" type="fill" paint={{ 'fill-color': '#D4A843', 'fill-opacity': 0.35 }} />
          <Layer id="bunkers-line" type="line" paint={{ 'line-color': '#C4963A', 'line-opacity': 0.6, 'line-width': 1 }} />
        </Source>

        {/* Water hazards */}
        <Source id="hazards" type="geojson" data={hazardsGeoJSON}>
          <Layer id="hazards-fill" type="fill" paint={{ 'fill-color': '#2563EB', 'fill-opacity': 0.25 }} />
          <Layer id="hazards-line" type="line" paint={{ 'line-color': '#3B82F6', 'line-opacity': 0.6, 'line-width': 1 }} />
        </Source>

        {/* Fairway path */}
        {layerData.fairway && (
          <Source id="fairway" type="geojson" data={layerData.fairway}>
            <Layer id="fairway-line" type="line" paint={{ 'line-color': '#ffffff', 'line-opacity': 0.3, 'line-width': 2 }} />
          </Source>
        )}

        {/* Green circle */}
        {layerData.greenCircle && (
          <Source id="green-circle" type="geojson" data={layerData.greenCircle}>
            <Layer id="green-circle-fill" type="fill" paint={{ 'fill-color': '#22c55e', 'fill-opacity': 0.12 }} />
            <Layer id="green-circle-line" type="line" paint={{ 'line-color': '#22c55e', 'line-opacity': 0.5, 'line-width': 1.5 }} />
          </Source>
        )}

        {/* Distance lines (aimpoint, user lines) */}
        <Source id="dist-lines" type="geojson" data={linesGeoJSON}>
          <Layer id="aim-green-line" type="line"
            filter={['==', ['get', 'type'], 'aim-to-green']}
            paint={{ 'line-color': '#f59e0b', 'line-width': 2, 'line-opacity': 0.6, 'line-dasharray': [2, 2] }} />
          <Layer id="user-aim-line" type="line"
            filter={['==', ['get', 'type'], 'user-to-aim']}
            paint={{ 'line-color': '#ffffff', 'line-width': 2.5, 'line-opacity': 0.8 }} />
          <Layer id="user-green-line" type="line"
            filter={['==', ['get', 'type'], 'user-to-green']}
            paint={{ 'line-color': '#ffffff', 'line-width': 2.5, 'line-opacity': 0.85 }} />
        </Source>

        {/* Shot trail */}
        {shotTrailData?.line && (
          <Source id="shot-trail-line" type="geojson" data={shotTrailData.line}>
            <Layer id="shot-trail" type="line"
              paint={{ 'line-color': '#a855f7', 'line-width': 2, 'line-opacity': 0.5, 'line-dasharray': [2, 2] }} />
          </Source>
        )}

        {/* Shot markers */}
        {shotTrailData?.markers?.map((s, i) => (
          <Marker key={`shot-${i}`} longitude={s.lng} latitude={s.lat} anchor="center">
            <div className="flex flex-col items-center">
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#a855f7', border: '1.5px solid #fff' }} />
              <div style={{ background: 'rgba(168,85,247,0.9)', color: '#fff', padding: '1px 5px', borderRadius: 6, fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap', marginTop: 2 }}>
                {i + 1}·{s.club}
              </div>
            </div>
          </Marker>
        ))}

        {/* 150 yard label */}
        {label150 && (
          <Marker longitude={label150.lng} latitude={label150.lat} anchor="center">
            <div style={{ background: 'rgba(250,204,21,0.85)', color: '#000', padding: '1px 6px', borderRadius: 8, fontSize: 9, fontWeight: 900, whiteSpace: 'nowrap' }}>150</div>
          </Marker>
        )}

        {/* Tee marker */}
        {teePos && (
          <Marker longitude={teePos.lng} latitude={teePos.lat} anchor="center">
            <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#ef4444', border: '2px solid #fff', boxShadow: '0 0 6px rgba(0,0,0,0.5)' }} />
          </Marker>
        )}

        {/* Green flag marker */}
        {greenPos && (
          <Marker longitude={greenPos.lng} latitude={greenPos.lat} anchor="bottom">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
              <line x1="5" y1="5" x2="5" y2="40" stroke="white" strokeWidth="2.5" />
              <polygon points="5,5 25,12 5,19" fill="#FACC15" stroke="#fff" strokeWidth="1" />
            </svg>
          </Marker>
        )}

        {/* Aimpoint marker (draggable) */}
        {aimpoint && greenPos && (
          <Marker
            longitude={aimpoint.lng}
            latitude={aimpoint.lat}
            anchor="center"
            draggable={true}
            onDragStart={() => setIsDraggingAim(true)}
            onDragEnd={(e) => {
              setIsDraggingAim(false);
              setAimpoint({ lat: e.lngLat.lat, lng: e.lngLat.lng });
            }}
          >
            <div style={{ cursor: 'grab' }}>
              <svg width="24" height="24" viewBox="-12 -12 24 24">
                <line x1="-8" y1="0" x2="8" y2="0" stroke="#f59e0b" strokeWidth="3" />
                <line x1="0" y1="-8" x2="0" y2="8" stroke="#f59e0b" strokeWidth="3" />
                <circle r="3" fill="#f59e0b" opacity="0.3" />
              </svg>
            </div>
          </Marker>
        )}

        {/* Distance label markers */}
        {distanceLabels.map(lbl => (
          <Marker key={lbl.id} longitude={lbl.lng} latitude={lbl.lat} anchor="center">
            <div style={{
              background: lbl.bg, color: lbl.color, padding: '2px 8px', borderRadius: 10,
              fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap', border: `1px solid ${lbl.border}`
            }}>
              {lbl.text}
            </div>
          </Marker>
        ))}

        {/* User position */}
        {userPos && (
          <Marker longitude={userPos.lng} latitude={userPos.lat} anchor="center">
            <div style={{
              width: 18, height: 18, borderRadius: '50%', background: '#3b82f6',
              border: '3px solid #fff', boxShadow: '0 0 8px rgba(59,130,246,0.6)'
            }} />
          </Marker>
        )}

        {/* Reference markers: show other holes when placing a green */}
        {referenceMarkers.map(m => (
          <Marker key={`ref-${m.hole}`} longitude={m.lng} latitude={m.lat} anchor="center">
            <div style={{
              width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: m.source === 'osm' ? 'rgba(34,197,94,0.7)' : 'rgba(250,204,21,0.7)',
              border: '1.5px solid #fff', fontSize: 9, fontWeight: 900, color: '#fff',
              boxShadow: '0 0 4px rgba(0,0,0,0.4)'
            }}>
              {m.hole}
            </div>
          </Marker>
        ))}

        {/* Pending green marker (waiting for confirmation) */}
        {pendingGreen && (
          <Marker longitude={pendingGreen.lng} latitude={pendingGreen.lat} anchor="bottom">
            <div className="animate-pulse">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
                <line x1="5" y1="5" x2="5" y2="40" stroke="#facc15" strokeWidth="2.5" />
                <polygon points="5,5 25,12 5,19" fill="#facc15" stroke="#fff" strokeWidth="1" />
              </svg>
            </div>
          </Marker>
        )}
      </Map>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
            <span className="text-white/70 text-sm font-medium">Loading course data...</span>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/90 via-black/70 to-transparent">
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center" data-testid="close-gps-map"><ArrowLeft className="w-5 h-5 text-white" /></button>
          <div className="flex items-center gap-4">
            <button onClick={() => goToHole(Math.max(1, currentHole - 1))} disabled={currentHole <= 1} className="text-white/60 disabled:opacity-20" data-testid="gps-prev-hole"><ChevronLeft className="w-6 h-6" /></button>
            <div className="text-center min-w-[80px]">
              <div className="flex items-center gap-1.5 justify-center"><Flag className="w-3.5 h-3.5 text-yellow-400" /><span className="text-white font-black text-xl tracking-tight">{currentHole}</span></div>
              <div className="text-white/50 text-[10px] font-semibold mt-0.5">Par {holeData.par || '-'} · {holeData.yardage || '-'} yds</div>
            </div>
            <button onClick={() => goToHole(Math.min(totalHoles, currentHole + 1))} disabled={currentHole >= totalHoles} className="text-white/60 disabled:opacity-20" data-testid="gps-next-hole"><ChevronRight className="w-6 h-6" /></button>
          </div>
          <button onClick={recenter} className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center" data-testid="recenter-gps"><Crosshair className="w-5 h-5 text-white" /></button>
        </div>
        <div className="flex gap-[3px] justify-center pb-3 px-6">
          {Array.from({ length: totalHoles }, (_, i) => i + 1).map(h => (
            <button key={h} onClick={() => goToHole(h)} className={`h-1.5 rounded-full transition-all ${h === currentHole ? 'bg-yellow-400 w-4' : 'bg-white/25 w-1.5'}`} data-testid={`gps-hole-nav-${h}`} />
          ))}
        </div>
      </div>

      {/* Place green prompt (no pending tap yet) */}
      {placingGreen && !greenPos && !pendingGreen && (
        <div className="absolute top-28 left-1/2 -translate-x-1/2 z-10" data-testid="set-green-prompt">
          <div className="bg-black/85 backdrop-blur-lg rounded-2xl px-5 py-3 text-center border border-yellow-500/30 shadow-2xl max-w-[260px]">
            <Flag className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
            <div className="text-sm font-bold text-white">Tap the green for Hole {currentHole}</div>
            <div className="text-[10px] text-white/40 mt-0.5">Zoom in and tap the center of the green</div>
            <div className="text-[9px] text-white/25 mt-1">Numbered circles show other mapped holes</div>
            <button
              onClick={() => {
                const map = mapRef.current;
                if (map && courseLat && courseLng) {
                  map.flyTo({ center: [courseLng, courseLat], zoom: 16, bearing: 0, duration: 800 });
                }
              }}
              className="mt-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 text-[11px] font-semibold px-3 py-1 rounded-lg transition-colors"
              data-testid="center-on-course"
            >
              Center on Course
            </button>
          </div>
        </div>
      )}

      {/* Green placement confirmation dialog */}
      {pendingGreen && (
        <div className="absolute top-28 left-1/2 -translate-x-1/2 z-10" data-testid="confirm-green-prompt">
          <div className="bg-black/85 backdrop-blur-lg rounded-2xl px-5 py-3 text-center border border-yellow-500/30 shadow-2xl max-w-[280px]">
            <Flag className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
            <div className="text-sm font-bold text-white">Is this the green for Hole {currentHole}?</div>
            <div className="text-[10px] text-white/40 mt-0.5">The pulsing flag shows where you tapped</div>
            <div className="flex items-center justify-center gap-3 mt-3">
              <button
                onClick={cancelPendingGreen}
                className="bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors flex items-center gap-1.5"
                data-testid="cancel-green-placement"
              >
                <Undo2 className="w-3.5 h-3.5" />
                Redo
              </button>
              <button
                onClick={confirmGreen}
                className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors flex items-center gap-1.5"
                data-testid="confirm-green-placement"
              >
                <Check className="w-3.5 h-3.5" />
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Distance display panel */}
      {distToGreen !== null && (
        <div className="absolute bottom-36 left-3 z-10" data-testid="distance-display">
          {distToAim !== null && distAimToGreen !== null && distAimToGreen > 5 && (
            <div className="bg-black/85 backdrop-blur-md rounded-2xl overflow-hidden border border-amber-500/20 w-[68px] shadow-xl mb-2">
              <div className="py-1.5 px-1 text-center border-b border-amber-500/10">
                <div className="text-[7px] text-amber-300/60 font-bold uppercase tracking-wider">Target</div>
                <div className="text-base font-black text-amber-400 leading-tight">{distToAim}</div>
              </div>
              <div className="py-1.5 px-1 text-center">
                <div className="text-[7px] text-amber-300/40 font-bold uppercase tracking-wider">To Pin</div>
                <div className="text-sm font-black text-amber-400/70 leading-tight">{distAimToGreen}</div>
              </div>
            </div>
          )}
          <div className="bg-black/85 backdrop-blur-md rounded-2xl overflow-hidden border border-white/10 w-[68px] shadow-xl">
            <div className="py-2 px-1 text-center border-b border-white/10">
              <div className="text-[7px] text-red-300/60 font-bold uppercase tracking-wider">Back</div>
              <div className="text-base font-black text-red-400 leading-tight">{distBack}</div>
            </div>
            <div className="py-2.5 px-1 text-center border-b border-white/10 bg-white/5">
              <div className="text-[7px] text-white/40 font-bold uppercase tracking-wider">Center</div>
              <div className="text-xl font-black text-white leading-tight">{distToGreen}</div>
            </div>
            <div className="py-2 px-1 text-center">
              <div className="text-[7px] text-green-300/60 font-bold uppercase tracking-wider">Front</div>
              <div className="text-base font-black text-green-400 leading-tight">{distFront}</div>
            </div>
          </div>
          {hasPreciseGreen && (
            <div className="mt-1.5 text-center"><div className="bg-green-500/20 rounded-full px-2 py-0.5 inline-block"><span className="text-[7px] text-green-300 font-bold">MAPPED</span></div></div>
          )}
        </div>
      )}

      {/* Club recommendation */}
      {clubRecommendation && distToGreen > 10 && !showShotTracker && !placingGreen && (
        <div className="absolute bottom-36 right-3 z-10" data-testid="club-rec-floating">
          <div className="bg-black/85 backdrop-blur-md rounded-2xl overflow-hidden border border-emerald-500/20 shadow-xl px-3 py-2 text-center">
            <div className="text-[7px] text-emerald-300/60 font-bold uppercase tracking-wider">Club</div>
            <div className="text-base font-black text-emerald-400 leading-tight">{clubRecommendation.recommendation}</div>
            <div className="text-[8px] text-emerald-300/40">~{clubRecommendation.avg_distance} yds</div>
          </div>
        </div>
      )}

      {/* Shot Tracker overlay */}
      {showShotTracker && (
        <ShotTracker
          onLogShot={handleLogShot}
          onCancel={() => setShowShotTracker(false)}
          recommendation={clubRecommendation}
          distToGreen={distToGreen}
        />
      )}

      {/* Right side controls */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-2.5">
        <button onClick={toggleHeadingMode} className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-lg ${headingMode === 'hole' ? 'bg-blue-600/80 border border-blue-400/40 backdrop-blur-sm' : 'bg-black/70 border border-white/15 backdrop-blur-sm'}`} data-testid="toggle-heading" title={headingMode === 'hole' ? 'Hole-aligned (tap for North)' : 'North-up (tap for hole-aligned)'}>
          <Compass className="w-5 h-5 text-white" />
        </button>
        <button onClick={recenterMap} className="w-10 h-10 rounded-full bg-black/70 border border-white/15 backdrop-blur-sm flex items-center justify-center shadow-lg" data-testid="recenter-map" title="Re-center view">
          <Navigation2 className="w-5 h-5 text-white" />
        </button>
        <button onClick={() => setShowArcs(!showArcs)} className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-lg ${showArcs ? 'bg-white/20 border border-white/30 backdrop-blur-sm' : 'bg-black/70 border border-white/15 backdrop-blur-sm'}`} data-testid="toggle-arcs" title="Distance arcs"><CircleDot className="w-5 h-5 text-white" /></button>
        {aimpoint && (<button onClick={resetAimpoint} className="w-10 h-10 rounded-full bg-black/70 border border-white/15 backdrop-blur-sm flex items-center justify-center shadow-lg" data-testid="reset-aimpoint" title="Reset target"><Target className="w-5 h-5 text-amber-400" /></button>)}
        {greenPos && !hasOsmHole && (
          <>
            <button onClick={moveGreen} className="w-10 h-10 rounded-full bg-black/70 border border-white/15 backdrop-blur-sm flex items-center justify-center shadow-lg" data-testid="redo-green" title="Re-place green"><Undo2 className="w-4 h-4 text-yellow-400" /></button>
            <button onClick={deleteGreenMarker} className="w-10 h-10 rounded-full bg-black/70 border border-red-500/30 backdrop-blur-sm flex items-center justify-center shadow-lg" data-testid="delete-green" title="Remove green marker"><Trash2 className="w-4 h-4 text-red-400" /></button>
          </>
        )}
      </div>

      {/* Bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/95 via-black/70 to-transparent pt-10 pb-5 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold shadow-md">{(activeRound?.player_name || 'Me').slice(0, 2).toUpperCase()}</div>
            <div>
              <div className="text-white text-sm font-semibold">{activeRound?.player_name || 'You'}</div>
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${gpsAccuracy && gpsAccuracy <= 10 ? 'bg-green-400' : gpsAccuracy && gpsAccuracy <= 30 ? 'bg-yellow-400' : 'bg-red-400'}`} />
                <span className="text-[10px] text-white/40">{gpsAccuracy ? `±${gpsAccuracy}m` : 'GPS...'}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowShotTracker(true)}
              disabled={!userPos}
              className="bg-purple-600 hover:bg-purple-700 disabled:opacity-30 px-4 py-2.5 rounded-xl text-white text-sm font-bold transition-colors shadow-lg shadow-purple-900/30 flex items-center gap-1.5"
              data-testid="gps-log-shot"
            >
              <Locate className="w-4 h-4" />
              Shot {((shots || []).filter(s => s.hole_number === currentHole).length || 0) + 1}
            </button>
            <button onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700 px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors shadow-lg shadow-emerald-900/30" data-testid="gps-add-score">Score</button>
          </div>
        </div>
        {!placingGreen && greenPos && (
          <div className="text-center mt-2 text-[10px] text-white/30">
            {showShotTracker ? 'Select the club you used' : aimpoint ? 'Tap map to measure · Log Shot to track clubs' : 'Tap map to measure distance'}
            {osmData?.source === 'osm' && osmData.holes.length > 0 && ` · ${osmData.holes.length} holes`}
          </div>
        )}
      </div>
    </div>
  );
};
